import React, { useState, useEffect, use } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { cleanGuestSelection, setCustomerInfo, setStaffInfo } from 'redux/actions'
import Button from 'components/Button';
import Message from './components/Message';
import Audio from './components/Audio';
import QueueAnim from 'rc-queue-anim'
import { useLazyQuery } from '@apollo/client';
import { GET_TENANT_INFO_BY_TENANT_ID, GET_STAFF_INFO_BY_STAFF_ID } from 'graphql/service';
import {
  Content,
  ContentContainer,
  CommunicationContainer,
  TitleContainer,
} from './styles';
import ChatIcon from 'assets/icons/chat.svg'
import SendButton from 'assets/svg/sendButton.svg'
import CallButton from 'assets/svg/callButton.svg'
import colors from 'constants/colors';
import { debouncedErrorMessage } from 'utils/messageUtil';

const Communications = ({ mobile, open }) => {

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [activeClass, setActiveClass] = useState('Emails')
  const [content, setContent] = useState('')

  const staffInfo = useSelector(state => state.staffInfoReducer.staffInfo)
  const staffId= useParams().staffId

  const customerInfo = useSelector(state => state.customerInfoReducer.customerInfo)
  const tenantId = useParams().tenantId 
  const type = useSelector(state => state.selectReducer.selectedGuest?.type)

  useEffect(()=>{
    window.scrollTo(0, document.body.scrollHeight)
  }, [])
  
  useEffect(() => {
    if(type) {
      setActiveClass(type)
    }
  },[type])

  const [getTenantInfoByTenantId, { data: tenantInfoData, error: tenantInfoError }] = useLazyQuery(GET_TENANT_INFO_BY_TENANT_ID, {
    variables: { tenantId: tenantId }
  })

  const [getStaffInfoByStaffId, { data: staffInfoData, error: staffInfoError }] = useLazyQuery(GET_STAFF_INFO_BY_STAFF_ID, {
    variables: { staffId: staffId }
  })

  useEffect(() => {
    if (tenantId) {
      getTenantInfoByTenantId();
    }
  }, [tenantId, getTenantInfoByTenantId]);

  useEffect(() => {
    if (!tenantInfoData?.tenantByID) return;

    const tenant = tenantInfoData.tenantByID;
    const newCustomer = {
      ...customerInfo,
      userId: tenant?.user?.userId || '',
      tenantId,
      fullName: tenant?.user?.fullName || '',
      email: tenant?.user?.email || '',
      phoneNumber: tenant?.user?.phoneNumber || '',
      streetAddress: tenant?.user?.address || '',
      streetAddressLine2: tenant?.user?.addressLine2 || '',
      city: tenant?.user?.city || '',
      province: tenant?.user?.province || '',
      postalCode: tenant?.user?.postalCode || '',
      country: tenant?.user?.country || '',
      dateOfBirth: tenant?.user?.dateOfBirth || '',
      travelDocumentNumber: '', // FIXME: document value
      travelDocumentType: '',   // FIXME: document value
    };

    dispatch(setCustomerInfo(newCustomer));
  }, [tenantInfoData, tenantId, customerInfo, dispatch]);

  useEffect(() => {
    if (staffId) {
      getStaffInfoByStaffId();
    }
  }, [staffId, getStaffInfoByStaffId]);

  useEffect(() => {
    if (!staffInfoData?.staffByID) return;

    const {
      user: {
        userId,
        fullName,
        email,
        phoneNumber,
        address,
        addressLine2,
        city,
        province,
        postalCode,
        country,
      },
    } = staffInfoData.staffByID;

    const newStaff = {
      ...staffInfo,
      userId,
      staffId,
      fullName,
      email,
      phoneNumber,
      streetAddress: address,
      streetAddressLine2: addressLine2,
      city,
      province,
      postalCode,
      country,
      // roles: roles,   // Uncomment if needed
      // status: status, // Uncomment if needed
    };

    dispatch(setStaffInfo(newStaff));
  }, [staffInfoData, staffId, staffInfo, dispatch]);

  useEffect(() => {
    if (!tenantInfoError && !staffInfoError) return;

    if (!tenantInfoError.networkError && !staffInfoError.networkError) {
      let content = tenantInfoError.message || staffInfoError.message;

      if (tenantInfoError.message.includes('Validation') || staffInfoError.message.includes('Validation')) {
        content = JSON.stringify(
          tenantInfoError.graphQLErrors[0]?.extensions?.exception || staffInfoError.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [tenantInfoError, staffInfoError]);

  return (
    <CommunicationContainer mobile={mobile} open={open}>
      <Helmet>
        <title>Communications - Rook Hotel PMS</title>
        <meta name="description" content="Communications - Rook Hotel PMS" />
      </Helmet>
      <Content>
        <TitleContainer>
          <Button
            onClick={()=>{
              if(!mobile) {
                navigate(-1)
              } else {
                dispatch(cleanGuestSelection())
              }
            }}
            additionalstyle={`
              position: absolute;
              left: 30px;
              font-size: 16px;
              font-weight: bold;
              color: ${colors.lightBlue};
              background-color: transparent;

              @media only screen and (max-width: 767px) {
                font-size: 13px;
              }
            `}
          >
            {'<'} Back
          </Button>
          <div>
            <img src={ChatIcon} alt={'chat'}/>
            {`Communications with ${tenantId? customerInfo.fullName: staffInfo.fullName}`}
          </div>
        </TitleContainer>
        <ContentContainer>
          <div className='navigators'>
            <div
              className={activeClass==='Emails'? 'active': null}
              onClick={()=> {
                setActiveClass('Emails')
              }}
            >
              Emails
            </div>
            <div
              className={activeClass==='Chats'? 'active': null}
              onClick={()=> {
                setActiveClass('Chats')
              }}
            >
              Chats
            </div>
            <div
              className={activeClass==='Text'? 'active': null}
              onClick={()=> {
                setActiveClass('Text')
              }}
            >
              Text Message
            </div>
            <div
              className={activeClass==='Phone Calls'? 'active': null}
              onClick={()=> {
                setActiveClass('Phone Calls')
              }}
            >
              Phone Calls
            </div>
          </div>
          {
            activeClass==='Phone Calls' ? (
              <>
                <div className='audios'>
                  <QueueAnim type='alpha' delay={100}>
                    <Audio
                      createAt={Date.now()}
                      path={"https://file-examples.com/storage/fe88505b6162b2538a045ce/2017/11/file_example_MP3_1MG.mp3"}
                    />
                    <Audio
                      staff
                      createAt={Date.now()+1000}
                      path={"https://file-examples.com/storage/feffcabf3562ab2f72eb1aa/2017/11/file_example_MP3_1MG.mp3"}
                    />
                    <Audio
                      createAt={Date.now()+2000}
                      path={"https://file-examples.com/storage/feffcabf3562ab2f72eb1aa/2017/11/file_example_MP3_1MG.mp3"}
                    />
                    <div style={{clear: 'both'}}></div>
                  </QueueAnim>
                </div>
                <div className='callBar'>
                  <div></div>
                  <img src={CallButton} alt={'callButton'} />
                </div>
              </>
            ): (
              <>
                <div className='messages'>
                  <QueueAnim type='alpha' delay={100}>
                    <Message
                      createAt={Date.now()}
                      content={'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'}
                    />
                    <Message
                      staff
                      createAt={Date.now()}
                      content={'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'}
                    />
                    <Message
                      createAt={Date.now()}
                      content={'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'}
                    />
                    <div style={{clear: 'both'}}></div>
                  </QueueAnim>
                </div>
                <div className='typeBar'>
                  <input
                    value={content}
                    onChange={(e)=>{
                      setContent(e.target.value)
                    }}
                  />
                  <img src={SendButton} alt={'sendButton'} />
                </div>
              </>
            )
          }
        </ContentContainer>
      </Content>
    </CommunicationContainer>
  )
}

export default Communications;
